import { TooltipWrapper } from "@src/components/deprecated/DeprecatedTooltip/TooltipWrapper";
import { Popup } from "@src/components/deprecated/Popup/Popup";
import { TagsSelectPopup } from "@src/components/deprecated/TagsSelectPopup/TagsSelectPopup";
import { Stack } from "@src/components/ui-components/Stack/Stack";
import { formatTagName } from "data-hooks/company/tags/tags.utils";
import { t } from "i18next";
import { DataTableData, DataTableHeader } from "../../../../../components/deprecated/DataTable/DataTable";
import { FileLabel } from "../../../../../components/deprecated/FilePopup/FileLabel";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { StatusTag, StatusTagColor } from "../../../../../components/deprecated/StatusTag/StatusTag";
import { FileTypeImageView } from "../../../../../components/ui-components/FileTypeImageView/FileTypeImageView";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { ActionValidator } from "../../../../../auth/actionValidator";
import { FileUtilities } from "../../../../../utils/fileUtilities";
import { ContractorGroupData } from "../../../ContractorGroup/ContractorGroupData";
import { PropertyData } from "../../../Property/PropertyData";
import { UnitData } from "../../../Property/Units/UnitData";
import { Role } from "../../../User/Role";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { FileDocumentData } from "../../FileDocumentData";
import { LocalData } from "../../LocalData";
import styles from "./FileDocumentDataHelper.module.css";

export class FileDocumentDataHelper extends DataObjectHelper<FileDocumentData> {
  id = () => "fileDocument";

  allFields(): DataObjectField<any, FileDocumentData>[] {
    return [];
  }
  displayName = () => t("main.file");

  deleteTapped(file: FileDocumentData) {
    PopupTemplates.presentVerifyDelete(file.name, () => {
      ToastUtilities.presentPromise(`${t("main.deleting")} ${file.name}`, file.deleteFile());
    });
  }

  selectTagsTapped(file: FileDocumentData) {
    Popup.present((id) => (
      <TagsSelectPopup
        id={id}
        propertyId={file?.parent?.properties?.map((property) => property.id)}
        value={file.tags.map((tag) => tag.id)}
        onChange={(tags) => {
          ToastUtilities.presentSavingToast(
            file.saveUpdate({
              "tags.standard": tags.map((tag) => tag.id),
            })
          );
        }}
      />
    ));
  }

  override getActionsForData = (file: FileDocumentData): DataObjectAction<FileDocumentData>[] => {
    const actions: DataObjectAction<FileDocumentData>[] = [];

    let property: PropertyData | undefined = undefined;
    if (file.parent?.parent instanceof PropertyData) property = file.parent.parent;
    else if (file.parent instanceof PropertyData) property = file.parent;

    actions.push({
      id: "download",
      title: t("main.download"),
      iconName: "file_download",
      iconColor: "var(--primary-color)",
      performAction: () =>
        new Promise<void>((resolve) => {
          file.downloadFile();
          resolve();
        }),
    });

    if (
      file.parent instanceof UnitData &&
      ActionValidator.isAllowedToPerformAction("archive_tenant_unit_file", [file.parent.parent as PropertyData])
    ) {
      if (!file.archived) {
        actions.push({
          id: "archive",
          iconName: "archive",
          title: t("main.archiveTenantUnitFile"),
          requiredConsent: (dataObjects) => {
            return new Promise((resolve) => {
              PopupTemplates.presentVerifyAction(t("main.archiveTenantUnitFile") + "?", t("main.tenantUnitFileArchivationCaution"), () => {
                resolve({ dataObjects });
              });
            });
          },
          performAction: () => {
            return ToastUtilities.presentSavingToast(file.archive());
          },
        });
      } else {
        actions.push({
          id: "unarchive",
          iconName: "archive",
          title: t("main.unarchiveTenantUnitFile"),
          requiredConsent: (dataObjects) => {
            return new Promise((resolve) => {
              PopupTemplates.presentVerifyAction(t("main.unarchiveTenantUnitFile") + "?", t("main.tenantUnitFileDearchivationCaution"), () => {
                resolve({ dataObjects });
              });
            });
          },
          performAction: () => {
            return ToastUtilities.presentSavingToast(file.archive());
          },
        });
      }
    }

    if (file.parent instanceof ContractorGroupData || property?.members.getData(LocalData.currentUser.id)?.roles.includes(Role.LANDLORD)) {
      actions.push({
        id: "delete",
        title: t("main.delete"),
        iconName: "delete",
        iconColor: "var(--main-red)",
        performAction: () =>
          new Promise<void>((resolve) => {
            this.deleteTapped(file);
            resolve();
          }),
      });
    }

    return actions;
  };

  getAllDataTableDatas(documents: FileDocumentData[]): DataTableData[] {
    return documents.map((file, index) => {
      return {
        key: file.id,
        cells: {
          type: { element: <FileTypeImageView className={styles.fileTypeImage} fileDocument={file} />, rawString: file.type },
          tags: {
            element: (
              <Stack spacing="4px" style={{ flexWrap: "wrap" }}>
                {file.tags.map((tag) => (
                  <TooltipWrapper
                    key={tag.id}
                    trigger={<StatusTag color={tag.badgeColor as StatusTagColor} text={formatTagName(tag)} />}
                    title={tag.description}
                  />
                ))}
                <button onClick={() => this.selectTagsTapped(file)}>{t("main.select")}</button>
              </Stack>
            ),
            rawString: file.tags.map((tag) => formatTagName(tag)).join(", "),
          },
          name: { element: <FileLabel dataCy={`file-label-${index}`} file={file} otherFiles={documents} />, rawString: file.name, rawData: file },
          size: FileUtilities.getFileSizeString(file.size),
          visibility: FileUtilities.getFileDocumentVisiblityString(file),
        },
      };
    });
  }

  getAllDataTableHeaders(): DataTableHeader[] {
    return [
      {
        key: "type",
        title: "",
        width: "30px",
      },
      {
        key: "name",
        title: t("main.name"),
      },
      {
        key: "tags",
        title: t("main.tags"),
      },
      {
        key: "size",
        title: t("main.fileSize_abb"),
      },
      { key: "visibility", title: t("main.visibleFor") },
      {
        key: "actions",
        title: t("main.actions"),
      },
    ];
  }
}
